<template>
    <div class="ContractExtractionReviewPanel">
        <Tabs
            v-if="canViewContractExtractionData"
            class="ContractExtractionReviewPanel__tabs"
            :selected-tab="selectedTab"
            @change="selectedTab = $event"
        >
            <template #tab-header-contractExtractionRequirements>
                <span
                    class="ContractExtractionReviewPanel__requirementsTabLabel"
                    :class="{ 'ContractExtractionReviewPanel__requirementsTabLabel--error': requirementsFormHasErrors }"
                >
                    <EidIcon
                        v-if="requirementsFormHasErrors"
                        :icon-src="faTriangleExclamation"
                    />
                    Requirements
                </span>
            </template>
            <Tab id="contractExtractionRequirements" name="Requirements">
                <div class="ContractExtractionReviewPanel__body">
                    <ContractExtractionRequirements
                        :rp-name="rpName"
                        :contract-extraction-id="contractExtractionId"
                        :criteria-status="criteriaStatus"
                        :requirement-types="requirementTypes"
                        :requirement-models="requirementModels"
                        :criterion-templates="criterionTemplates"
                        :custom-properties="customProperties"
                        :country-code="countryCode"
                        :can-manage-requirements="canManageRequirements"
                        :show-validation-errors="requirementsShowValidationErrors"
                        @has-errors-change="$emit('requirements-has-errors-change', $event)"
                    />
                </div>
            </Tab>
            <template #tab-header-contractExtractionEntity>
                <span
                    class="ContractExtractionReviewPanel__entityTabLabel"
                    :class="{ 'ContractExtractionReviewPanel__entityTabLabel--error': entityFormHasErrors || entitySearchHasError }"
                >
                    <EidIcon
                        v-if="entityFormHasErrors || entitySearchHasError"
                        :icon-src="faTriangleExclamation"
                    />
                    Entity
                </span>
            </template>
            <Tab id="contractExtractionEntity" name="Entity">
                <div class="ContractExtractionReviewPanel__body">
                    <div class="ContractExtractionReviewPanel__entityToggleHeader">
                        <span class="ContractExtractionReviewPanel__entityToggleTitle">
                            {{ showExistingEntity ? 'Select Existing Entity' : 'New Entity Details' }}
                        </span>
                        <NewButton
                            color="primary"
                            variant="outline"
                            @click="onEntityModeToggle"
                        >
                            <EidIcon :icon-src="showExistingEntity ? faPlus : faBuilding" />
                            {{ showExistingEntity ? 'Create new instead' : 'Or assign to existing' }}
                        </NewButton>
                    </div>
                    <CreateEntity
                        v-show="!showExistingEntity"
                        :rp-name="rpName"
                        :contract-extraction-id="contractExtractionId"
                        :custom-properties="customProperties"
                        :requirement-types="entityRequirementTypes"
                        :requirement-definitions="requirementDefinitions"
                    />
                    <EntitySearchSelection
                        v-show="showExistingEntity"
                        show-address
                        :selected-entity-id="selectedEntityId"
                        :has-error="entitySearchHasError"
                        @select="onEntitySelect"
                    />
                </div>
            </Tab>
        </Tabs>
    </div>
</template>

<style lang="scss">
$tab-padding: 21px;

.ContractExtractionReviewPanel {
    background: var(--eidWhite);
    border: 1px solid var(--eidSecureBlue-10);
    border-radius: 8.75px;
    min-height: 100%;

    &__tabs {
        height: 100%;

        .Tabs__headerContainer {
            align-items: stretch;
            border-bottom: 1px solid var(--eidSecureBlue-10);
        }

        .Tabs__headers {
            flex: 1;
        }

        .Tabs__header {
            position: relative;
            flex: 1;
            border-bottom: 0;
            color: var(--eidTechGray);
            font-size: 16px;
            font-weight: 500;
            padding: $tab-padding;
            transition: color 0.25s ease;

            &::after {
                content: "";
                position: absolute;
                bottom: calc($tab-padding / 2 + 2px);
                left: $tab-padding;
                right: $tab-padding;
                border-bottom: 1px solid var(--eidTrustGreen);
                transform: scaleX(0);
                transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            }

            &--active, &:hover {
                color: var(--eidTrustGreen);
            }

            &--active::after {
                transform: scaleX(1);
            }
        }

        .Tabs__content {
            margin-top: 0;
            padding: $tab-padding;
        }
    }

    &__entityTabLabel,
    &__requirementsTabLabel {
        display: inline-flex;
        align-items: center;
        gap: 6px;

        &--error {
            color: var(--eidDangerRed);
        }
    }

    &__entityToggleHeader {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 16px;
    }

    &__entityToggleTitle {
        font-size: 16px;
        font-weight: 500;
        color: var(--eidSecureBlue);
    }

    &__body {
        font-size: 14px;
        line-height: 20px;
    }

    .EntitySearchSelection {
        height: 500px;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faPlus, faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
    import { faBuilding } from '@fortawesome/pro-regular-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { Tab, Tabs } from '@evidentid/dashboard-commons/components/Tabs';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { CriterionTemplate } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';
    import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { TprmRequirementModel } from '@evidentid/tprm-portal-lib/models/entity-details/TprmRequirementModel.model';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import EntitySearchSelection from '@/components/entity-search-selection/EntitySearchSelection.vue';
    import { RequirementLabels } from '@/components/migrated-compliance-section/migratedCompliance';
    import NewButton from '@/components/new-button/NewButton.vue';
    import ContractExtractionRequirements
        from '@/modules/contract-extraction/components/ContractExtractionRequirements/ContractExtractionRequirements.vue';
    import CreateEntity
        from '@/modules/contract-extraction/components/create-entity/CreateEntity.vue';
    import { EditableRequirementCriterion } from '@/modules/decisioning-criteria/models/EditableRequirements.model';

    interface ContractExtractionReviewCriteriaStatus {
        status: OperationStatus;
        originalCriteria: unknown[] | null;
        editableCriteria: EditableRequirementCriterion[] | null;
        editableEvaluationRuleRequirementTypes: TprmRequirementType[];
    }

    export default defineComponent({
        name: 'ContractExtractionReviewPanel',
        components: {
            CreateEntity,
            ContractExtractionRequirements,
            EntitySearchSelection,
            EidIcon,
            NewButton,
            Tab,
            Tabs,
        },
        props: {
            canViewContractExtractionData: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            rpName: {
                type: String,
                default: '',
            },
            contractExtractionId: {
                type: String,
                default: '',
            },
            criteriaStatus: {
                type: Object as PropType<ContractExtractionReviewCriteriaStatus>,
                required: true,
            },
            requirementTypes: {
                type: Array as PropType<TprmRequirementType[]>,
                default: (): TprmRequirementType[] => [],
            },
            requirementDefinitions: {
                type: Object as PropType<RequirementLabels>,
                default: (): RequirementLabels => ({}),
            },
            requirementModels: {
                type: Array as PropType<TprmRequirementModel[]>,
                default: (): TprmRequirementModel[] => [],
            },
            criterionTemplates: {
                type: Array as PropType<CriterionTemplate[]>,
                default: (): CriterionTemplate[] => [],
            },
            customProperties: {
                type: Array as PropType<CustomProperty[]>,
                default: (): CustomProperty[] => [],
            },
            entityRequirementTypes: {
                type: Array as PropType<string[]>,
                default: (): string[] => [],
            },
            countryCode: {
                type: String,
                default: 'US',
            },
            canManageRequirements: {
                type: Boolean,
                default: false,
            },
            entityFormHasErrors: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            entitySearchHasError: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            requirementsFormHasErrors: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            requirementsShowValidationErrors: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'entity-mode-change', 'entity-select', 'requirements-has-errors-change' ],
        data() {
            return {
                faBuilding,
                faPlus,
                faTriangleExclamation,
                selectedTab: 'contractExtractionRequirements',
                selectedEntityId: null as string | null,
                showExistingEntity: false,
            };
        },
        methods: {
            onEntitySelect(entityId: string | null): void {
                (this as any).selectedEntityId = entityId;
                this.$emit('entity-select', entityId);
            },
            onEntityModeToggle(): void {
                (this as any).showExistingEntity = !(this as any).showExistingEntity;
                this.$emit('entity-mode-change', (this as any).showExistingEntity);
            },
        },
    });
</script>
